import { useState } from 'react';
import { Package, MapPin, Filter, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRegionStore } from '@/stores/regionStore';
import { RegionSelector, ServiceCard, Loading, ErrorMessage, Button } from '@/components/common';
import type { ServiceWithPrice } from '@/types';

/**
 * ServicesPage Component
 * Browse available services by region
 */
export const ServicesPage = () => {
  const {
    selectedRegion,
    regionServices,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    setSelectedRegion,
    setPage,
  } = useRegionStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [filterType, setFilterType] = useState<string>('');

  // Filter services
  const filteredServices = regionServices.filter((service) => {
    const matchesSearch =
      searchQuery === '' ||
      service.nameEn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.nameAr?.includes(searchQuery) ||
      service.descriptionEn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.descriptionAr?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesActive =
      filterActive === null || service.isAvailable === filterActive;

    const matchesType = !filterType || service.serviceType === filterType;

    return matchesSearch && matchesActive && matchesType;
  });

  // Get unique service types from available services
  const serviceTypes = Array.from(
    new Set(regionServices.map((s) => s.serviceType))
  );

  const handleServiceClick = (service: ServiceWithPrice) => {
    // Here you could navigate to a service detail page or open a booking modal
    console.log('Selected service:', service);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterActive(null);
    setFilterType('');
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1);
    }
  };

  const hasFilters = searchQuery || filterActive !== null || filterType;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-primary">الخدمات المتاحة</h1>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Region Selection */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-primary" />
          <h2 className="font-semibold text-lg">اختر منطقتك</h2>
        </div>
        <RegionSelector
          value={selectedRegion?.id}
          onChange={setSelectedRegion}
          placeholder="اختر المنطقة لعرض الخدمات المتاحة..."
          showGovernorate
        />
        {selectedRegion && (
          <div className="mt-3 p-3 bg-dark rounded-lg border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">المنطقة المحددة:</p>
                <p className="font-semibold text-primary">
                  {selectedRegion.nameEn} - {selectedRegion.nameAr}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedRegion.governorateEn} - {selectedRegion.governorateAr}
                </p>
              </div>
              <Button
                onClick={() => setSelectedRegion(null)}
                variant="secondary"
                size="sm"
              >
                تغيير المنطقة
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Services List */}
      {selectedRegion ? (
        <>
          {/* Filters */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-5 w-5 text-primary" />
              <h2 className="font-semibold">فلاتر البحث</h2>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ابحث عن خدمة..."
                  className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2.5 pr-10 text-gray-200 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              </div>

              {/* Service Type Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
              >
                <option value="">جميع أنواع الخدمات</option>
                {serviceTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace('_', ' ').toUpperCase()}
                  </option>
                ))}
              </select>

              {/* Availability Filter */}
              <select
                value={
                  filterActive === null ? '' : filterActive ? 'true' : 'false'
                }
                onChange={(e) =>
                  setFilterActive(
                    e.target.value === ''
                      ? null
                      : e.target.value === 'true'
                  )
                }
                className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
              >
                <option value="">جميع الحالات</option>
                <option value="true">متاح فقط</option>
                <option value="false">غير متاح</option>
              </select>
            </div>

            {hasFilters && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-400">
                  عرض {filteredServices.length} من {regionServices.length} خدمة
                </div>
                <Button
                  onClick={handleClearFilters}
                  variant="secondary"
                  size="sm"
                >
                  مسح الفلاتر
                </Button>
              </div>
            )}
          </div>

          {/* Services Grid */}
          {loading ? (
            <Loading text="جاري تحميل الخدمات..." />
          ) : filteredServices.length === 0 ? (
            <div className="card text-center py-12">
              <Package className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg font-semibold">
                {regionServices.length === 0
                  ? 'لا توجد خدمات متاحة في هذه المنطقة'
                  : 'لا توجد خدمات مطابقة للفلاتر المحددة'}
              </p>
              {hasFilters && regionServices.length > 0 && (
                <Button
                  onClick={handleClearFilters}
                  variant="primary"
                  size="sm"
                  className="mt-4"
                >
                  مسح الفلاتر
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredServices.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onClick={handleServiceClick}
                  />
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="card mt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-400">
                      عرض صفحة {currentPage} من {totalPages} ({totalItems} خدمة إجمالاً)
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <ChevronRight className="h-4 w-4" />
                        السابق
                      </Button>
                      <div className="px-3 py-1.5 bg-dark-lighter rounded-lg border border-gray-700 text-sm font-medium">
                        {currentPage} / {totalPages}
                      </div>
                      <Button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        التالي
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className="card text-center py-12">
          <MapPin className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg font-semibold mb-2">
            اختر منطقتك لعرض الخدمات المتاحة
          </p>
          <p className="text-sm text-gray-500">
            حدد المنطقة من القائمة أعلاه لعرض الخدمات والأسعار المتاحة في منطقتك
          </p>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
